import { useFetcher, useLoaderData } from "react-router-dom";
import { INTENTS } from "~/utils/constants";
import { Form, Input, Button, Typography, Select, Radio, Alert } from "antd";
import * as APITypes from "~/API";
import { AddServiceRequestPageLoaderType } from "./loaders/AddServiceRequestPageLoader";
import { useAppTranslation } from "~/hooks/useAppTranslation";

const { Title } = Typography;

export const AddServiceRequestPage = () => {
  const loaderData = useLoaderData() as AddServiceRequestPageLoaderType;
  const { leaseOptions, defaultLeaseId } = loaderData;
  const { t } = useAppTranslation();
  const fetcher = useFetcher();
  /** `submitting` (action) then `loading` (revalidation) until `idle` — no local state. */
  const fetcherBusy = fetcher.state !== "idle";
  const formDisabled = fetcherBusy || leaseOptions.length === 0;

  const handleSubmit = (values: Record<string, unknown>) => {
    fetcher.submit(
      {
        intent: INTENTS._add_service_request,
        body: JSON.stringify(values),
      },
      { method: "post" }
    );
  };

  const [form] = Form.useForm();

  return (
    <div
      className="maintenance-request-form"
      style={{ maxWidth: "600px", margin: "0 auto", padding: "20px" }}
    >
      <Title
        className="maintenance-form-title"
        level={1}
        style={{ textAlign: "center", marginBottom: "20px" }}
      >
        {t("Submit_Maintenance_Request")}
      </Title>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        disabled={formDisabled}
        initialValues={{
          leaseId: defaultLeaseId ?? undefined,
        }}
      >
        {leaseOptions.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            message={t("No_active_lease_maintenance")}
            style={{ marginBottom: 16 }}
          />
        ) : null}

        <Form.Item
          className="maintenance-form-item"
          label={t("Maintenance_request_location")}
          name="leaseId"
          rules={[
            {
              required: true,
              message: t("No_option_selected"),
            },
          ]}
        >
          <Select
            options={leaseOptions.map((o) => ({
              value: o.leaseId,
              label: o.label,
            }))}
            placeholder={t("Select_an_entity")}
          />
        </Form.Item>

        <Form.Item
          className="maintenance-form-item"
          label={t("Title")}
          name="title"
          rules={[
            {
              required: true,
              message: t("Enter_a_title"),
            },
          ]}
        >
          <Input.TextArea />
        </Form.Item>

        <Form.Item
          className="maintenance-form-item"
          label={t("Problem_Type")}
          name="type"
          rules={[
            {
              required: true,
              message: t("No_option_selected"),
            },
          ]}
        >
          <Select>
            <Select.Option value={APITypes.ServiceRequestType.Plumbing}>
              {t(APITypes.ServiceRequestType.Plumbing)}
            </Select.Option>
            <Select.Option value={APITypes.ServiceRequestType.Electrical}>
              {t(APITypes.ServiceRequestType.Electrical)}
            </Select.Option>
            <Select.Option value={APITypes.ServiceRequestType.HeatingCooling}>
              {t(APITypes.ServiceRequestType.HeatingCooling)}
            </Select.Option>
            <Select.Option value={APITypes.ServiceRequestType.ApplianceError}>
              {t(APITypes.ServiceRequestType.ApplianceError)}
            </Select.Option>
            <Select.Option value={APITypes.ServiceRequestType.Toilet}>
              {t(APITypes.ServiceRequestType.Toilet)}
            </Select.Option>
            <Select.Option value={APITypes.ServiceRequestType.DoorWindow}>
              {t(APITypes.ServiceRequestType.DoorWindow)}
            </Select.Option>
            <Select.Option value={APITypes.ServiceRequestType.PestControl}>
              {t(APITypes.ServiceRequestType.PestControl)}
            </Select.Option>
            <Select.Option value={APITypes.ServiceRequestType.RoofCeiling}>
              {t(APITypes.ServiceRequestType.RoofCeiling)}
            </Select.Option>
            <Select.Option value={APITypes.ServiceRequestType.GeneralCleaning}>
              {t(APITypes.ServiceRequestType.GeneralCleaning)}
            </Select.Option>
            <Select.Option value={APITypes.ServiceRequestType.ExteriorLawn}>
              {t(APITypes.ServiceRequestType.ExteriorLawn)}
            </Select.Option>
            <Select.Option value={APITypes.ServiceRequestType.Other}>
              {t(APITypes.ServiceRequestType.Other)}
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          className="maintenance-form-item"
          label={t("Description")}
          name="description"
          rules={[
            {
              required: true,
              message: t("Enter_a_description"),
            },
          ]}
        >
          <Input.TextArea />
        </Form.Item>

        <Form.Item
          className="maintenance-form-item"
          label={t("Is_an_emergency")}
          name="emergency"
          rules={[
            {
              required: true,
              message: t("No_option_selected"),
            },
          ]}
        >
          <Radio.Group>
            <Radio value="true">{t("Yes")}</Radio>
            <Radio value="false">{t("No")}</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          className="maintenance-form-item"
          label={t("Is_upgrade_or_repair")}
          name="isUpgrade"
          rules={[
            {
              required: true,
              message: t("No_option_selected"),
            },
          ]}
        >
          <Radio.Group>
            <Radio value="true">{t("Upgrade")}</Radio>
            <Radio value="false">{t("Repair")}</Radio>
          </Radio.Group>
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          loading={fetcherBusy}
          block
          disabled={leaseOptions.length === 0}
        >
          {t("Submit")}
        </Button>
      </Form>
    </div>
  );
};
